// ============================================================================
// PAYMENT VOUCHER (ADR-0039) — the single outgoing-payment document.
//
// Settle a supplier bill, pay an advance, book a direct expense, pay something
// recoverable from a customer — all on one voucher, with withholding, discounts,
// input-tax adjustments and round-off as ordinary lines. The cash credited to
// the bank is derived, never typed.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption,
  ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import { EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan } from 'components/enterprise-shell';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { paymentService, partyService } from 'api/services';
import type { PaymentDto, PaymentQueryParams } from 'api/types';
import { PaymentVoucherFormModal, PaymentVoucherViewModal } from '../../../modals/modules/accounts/PaymentVoucherFormModal';

type ViewMode = 'list' | 'grid';

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
const money = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'paymentNumber', label: 'Payment No' },
  { id: 'paymentDate', label: 'Date' },
  { id: 'partyName', label: 'Paid To' },
  { id: 'paidFromLedgerName', label: 'Paid From' },
  { id: 'instrument', label: 'Instrument' },
  { id: 'grossAmount', label: 'Gross' },
  { id: 'deductionTotal', label: 'Deductions' },
  { id: 'netPaid', label: 'Net Paid' },
  { id: 'docStatus', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface ScreenProps { moduleIcon?: string; tabId?: string; moduleName?: string; }

const PaymentVoucherScreen: React.FC<ScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const can = useCan('payment-voucher');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<PaymentQueryParams>({ page: 1, pageSize: 25 });
  const [data, setData] = useState<PaymentDto[]>([]);
  const [pagination, setPagination] = useState({ totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('payment-voucher-columns', COLUMN_DEFS);

  // --- filter panel: draft values apply only on Apply (consistent with the reports) ---
  const [filterOpen, setFilterOpen] = useState(false);
  const [partyFilter, setPartyFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState<PaymentDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentService.getAll(queryParams);
      setData(res.data);
      setPagination({ totalItems: res.pagination.totalItems });
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message || 'Could not load payments.');
      setData([]);
    } finally { setLoading(false); }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleAdd = useCallback(() => {
    if (formOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormOpen(true);
  }, [formOpen, tabId, isMinimized]);

  const handleRestoreModal = useCallback((instanceId?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    setFormOpen(true);
  }, []);

  const handleView = useCallback((r: PaymentDto) => {
    const viewId = tabId && r.paymentNumber ? `${tabId}-view-modal-${r.paymentNumber}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setSelected(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((paymentNumber: string) => {
    const rec = data.find(p => p.paymentNumber === paymentNumber);
    if (rec) { setSelected(rec); setViewOpen(true); }
  }, [data]);

  const handleCancel = useCallback(async (r: PaymentDto) => {
    const ok = await confirmDelete({
      resourceLabel: `payment ${r.paymentNumber || ''}`, confirmLabel: 'Cancel Payment',
    });
    if (!ok) return;
    try {
      await paymentService.cancel(r.id, r.rowVersion);
      notificationService.toast.success('Payment cancelled.');
      load();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message || 'Could not cancel the payment.', 7000);
      if (e?.response?.status === 409) load();
    }
  }, [confirmDelete, load]);

  const columns: SyutGridColumn<PaymentDto>[] = useMemo(() => [
    {
      id: 'paymentNumber', header: 'Payment No', accessorKey: 'paymentNumber', size: 150,
      cell: ({ row }) => (row.original as PaymentDto).paymentNumber || '—',
    },
    {
      id: 'paymentDate', header: 'Date', accessorKey: 'paymentDate', size: 110,
      cell: ({ row }) => fmtDate((row.original as PaymentDto).paymentDate),
    },
    {
      id: 'partyName', header: 'Paid To', accessorKey: 'partyName', size: 200,
      cell: ({ row }) => (row.original as PaymentDto).partyName || '—',
    },
    {
      id: 'paidFromLedgerName', header: 'Paid From', accessorKey: 'paidFromLedgerName', size: 180,
      cell: ({ row }) => (row.original as PaymentDto).paidFromLedgerName || '—',
    },
    {
      id: 'instrument', header: 'Instrument', accessorKey: 'instrumentType', size: 150,
      cell: ({ row }) => {
        const p = row.original as PaymentDto;
        if (!p.instrumentType) return '—';
        return p.instrumentNumber ? `${p.instrumentType} · ${p.instrumentNumber}` : p.instrumentType;
      },
    },
    {
      id: 'grossAmount', header: 'Gross', accessorKey: 'grossAmount', size: 120,
      cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{money((row.original as PaymentDto).grossAmount)}</span>,
    },
    {
      id: 'deductionTotal', header: 'Deductions', accessorKey: 'deductionTotal', size: 120,
      cell: ({ row }) => {
        const d = (row.original as PaymentDto).deductionTotal;
        return <span style={{ display: 'block', textAlign: 'right', color: d ? '#16a34a' : 'var(--text-muted)' }}>{money(d)}</span>;
      },
    },
    {
      id: 'netPaid', header: 'Net Paid', accessorKey: 'netPaid', size: 140,
      cell: ({ row }) => {
        const p = row.original as PaymentDto;
        return <span style={{ display: 'block', textAlign: 'right', fontWeight: 600 }}>{money(p.netPaid)} {p.currencyCode || ''}</span>;
      },
    },
    {
      id: 'docStatus', header: 'Status', accessorKey: 'docStatus', size: 130,
      cell: ({ row }) => {
        const p = row.original as PaymentDto;
        return (
          <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center' }}>
            <span className="badge">{p.docStatus}</span>
            {p.icLink && <span className="badge" title="Posted with an intercompany pair">IC</span>}
          </span>
        );
      },
    },
  ], []);

  const rowActions = useCallback((r: PaymentDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    // A posted payment is never edited — cancel it and re-enter (ADR-0039).
    if (can('DELETE') && r.docStatus === 'POSTED') {
      items.push({
        id: 'cancel', label: 'Cancel', icon: <IconSystem.trash size={18} />,
        onClick: () => handleCancel(r), variant: 'danger',
      });
    }
    return items;
  }, [can, handleView, handleCancel]);

  const loadPartyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined });
    return [
      { value: '', label: 'All payees' },
      ...res.data.map(p => ({ value: p.id, label: `${p.partyCode} — ${p.partyName}` })),
    ];
  }, []);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'New Payment'), visible: can('CREATE') },
    actionButtonFactory.refresh(load),
  ];

  const appliedCount = [queryParams.partyId, queryParams.fromDate, queryParams.toDate, queryParams.includeCancelled]
    .filter(Boolean).length;

  const applyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      partyId: partyFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      includeCancelled: includeCancelled || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [partyFilter, fromDate, toDate, includeCancelled]);

  const clearFilters = useCallback(() => {
    setPartyFilter(''); setFromDate(''); setToDate(''); setIncludeCancelled(false);
    setQueryParams(p => ({ page: 1, pageSize: p.pageSize, searchTerm: p.searchTerm }));
  }, []);

  return (
    <EnterpriseScreenShell
      title="Payment Vouchers"
      description="One voucher for supplier bills, advances, direct expenses and recoverables — with deductions"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.dollarSign size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search payment no / payee / narration...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Paid To" isAsync loadOptions={loadPartyOptions} defaultOptions cacheOptions={false}
                  value={partyFilter} onChange={v => setPartyFilter((v as string) || '')}
                  placeholder="All payees" isClearable size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled}
                    onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            frozenRightColumns={['actions']}
            emptyMessage={loading ? 'Loading...' : 'No payments yet — click New Payment to post one.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.dollarSign size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'No payments yet.'}</p>
                </div>
              ) : data.map(p => (
                <div key={p.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {p.paymentNumber || '—'} · {fmtDate(p.paymentDate)}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>
                        {money(p.netPaid)} {p.currencyCode || ''}
                      </h3>
                    </div>
                    <ActionMenu items={rowActions(p)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {p.partyName || 'No payee'} · {p.paidFromLedgerName || '—'}
                  </div>
                  {p.deductionTotal > 0 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Gross {money(p.grossAmount)} less {money(p.deductionTotal)} deducted
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <span className="badge">{p.docStatus}</span>
                    {p.icLink && <span className="badge">IC</span>}
                  </div>
                </div>
              ))}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
              </div>
            )}
          </div>
        )}
      </div>

      <PaymentVoucherFormModal isOpen={formOpen}
        onPosted={load}
        onClose={() => setFormOpen(false)}
        tabId={tabId} moduleName={moduleName || 'Payment Vouchers'}
        createInstanceId={createInstanceId}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef} />

      <PaymentVoucherViewModal isOpen={viewOpen} payment={selected}
        onClose={() => { setViewOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Payment Vouchers'}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default PaymentVoucherScreen;
